import { useEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "@/lib/cx";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  label?: string;
}

/** Mesma duracao da transicao de saida do sheet (--motion-sheet). */
const EXIT_MS = 250;

/**
 * Bottom sheet para acoes contextuais (RF-UX-007): backdrop, foco preso e
 * devolvido ao gatilho (RF-UX-093/085), fecha por Escape/backdrop.
 * Entra/sai com transicao interrompivel (padrao Telegram), respeitando
 * `prefers-reduced-motion`.
 */
export function BottomSheet({ open, onClose, title, children, label }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(id);
    }
    setVisible(false);
    const id = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open || !mounted) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = sheetRef.current;
    node?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !node) return;
      const focusable = node.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open, mounted, onClose]);

  if (!mounted) return null;

  return (
    <>
      <div className={cx("backdrop", visible && "open")} onClick={onClose} aria-hidden="true" />
      <div className={cx("sheet-wrap", !visible && "closed")}>
        <div
          ref={sheetRef}
          className="sheet"
          role="dialog"
          aria-modal="true"
          aria-label={label ?? title}
          tabIndex={-1}
        >
          <div className="sheet-handle" aria-hidden="true" />
          <div className="sheet-title">{title}</div>
          {children}
        </div>
      </div>
    </>
  );
}

interface SheetActionProps {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  current?: boolean;
  trailing?: ReactNode;
  danger?: boolean;
}

export function SheetAction({ icon, children, onClick, current, trailing, danger }: SheetActionProps) {
  return (
    <button
      type="button"
      role={current === undefined ? undefined : "menuitemradio"}
      aria-checked={current}
      className={cx("sheet-action", danger && "text-danger")}
      onClick={onClick}
    >
      {icon ? (
        <span className="ic" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="flex-1">{children}</span>
      {trailing}
    </button>
  );
}
